import { useState, useEffect } from 'react';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { 
  Folder, FolderOpen, FileText, FileSpreadsheet, File, Lock, 
  Users, Eye, EyeOff, Search, ChevronRight, ChevronDown, Settings,
  FileImage, FileVideo, FileAudio, Shield, UserCheck
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const enconOrange = '#ee7203';

const FilesAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [privateFolders, setPrivateFolders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState({});

  // Permission editing state
  const [editVisibility, setEditVisibility] = useState('global');
  const [editAllowedUsers, setEditAllowedUsers] = useState([]);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      const [allFilesRes, privateFoldersRes] = await Promise.all([
        api.get(`${API}/api/admin/files/all`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        api.get(`${API}/api/admin/files/private-folders`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      setFolders(allFilesRes.data.folders || []);
      setFiles(allFilesRes.data.files || []);
      setUsers(allFilesRes.data.users || []);
      setPrivateFolders(privateFoldersRes.data.private_folders || []);
    } catch (error) {
      console.error('Error loading files data:', error);
      toast.error('Failed to load files data');
    } finally {
      setLoading(false);
    }
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  const openPermissionModal = (item, type) => {
    setSelectedItem(item);
    setSelectedItemType(type);
    setEditVisibility(item.visibility || 'global');
    setEditAllowedUsers(item.allowed_users || []);
    setPermissionModalOpen(true);
  };

  const savePermissions = async () => {
    if (!selectedItem) return;

    try {
      const endpoint = selectedItemType === 'folder'
        ? `${API}/api/files/folders/${selectedItem.id}/visibility`
        : `${API}/api/files/${selectedItem.id}/visibility`;

      await api.put(endpoint, {
        visibility: editVisibility,
        allowed_users: editAllowedUsers
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success('Permissions updated');
      setPermissionModalOpen(false);
      await loadAllData();
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast.error(error.response?.data?.detail || 'Failed to save permissions');
    }
  };

  const getFileIcon = (fileType, size = 'w-5 h-5') => {
    switch (fileType) {
      case 'pdf': return <FileText className={`${size} text-red-500`} />;
      case 'word': return <FileText className={`${size} text-blue-500`} />;
      case 'excel': return <FileSpreadsheet className={`${size} text-green-500`} />;
      case 'powerpoint': return <FileText className={`${size} text-orange-500`} />;
      case 'image': return <FileImage className={`${size} text-purple-500`} />;
      case 'video': return <FileVideo className={`${size} text-pink-500`} />;
      case 'audio': return <FileAudio className={`${size} text-yellow-500`} />;
      default: return <File className={`${size} text-gray-500`} />;
    }
  };

  const getVisibilityBadge = (visibility) => {
    switch (visibility) {
      case 'global':
      case null:
      case undefined:
        return <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">Global</span>;
      case 'restricted':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-700">Restricted</span>;
      case 'private':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-100 text-indigo-700">Private</span>;
      case 'hidden':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">Hidden</span>;
      default:
        return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">Unknown</span>;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Build folder tree structure
  const buildFolderTree = () => {
    const folderMap = {};
    const rootFolders = [];

    folders.forEach(folder => {
      folderMap[folder.id] = { ...folder, children: [], files: [] };
    });

    // Assign children to parents
    folders.forEach(folder => {
      if (folder.parent_id && folderMap[folder.parent_id]) {
        folderMap[folder.parent_id].children.push(folderMap[folder.id]);
      } else if (!folder.parent_id) {
        rootFolders.push(folderMap[folder.id]);
      }
    });

    // Assign files to folders
    files.forEach(file => {
      if (file.folder_id && folderMap[file.folder_id]) {
        folderMap[file.folder_id].files.push(file);
      }
    });

    return rootFolders;
  };

  const renderFolderTree = (folder, depth = 0) => {
    const isExpanded = expandedFolders[folder.id];
    const hasChildren = folder.children?.length > 0 || folder.files?.length > 0;
    const isPrivate = folder.is_private || folder.owner_id;

    // Apply search filter
    if (searchTerm) {
      const matchesSearch = folder.name.toLowerCase().includes(searchTerm.toLowerCase());
      const hasMatchingFiles = folder.files?.some(f => f.name.toLowerCase().includes(searchTerm.toLowerCase()));
      const hasMatchingChildren = folder.children?.some(c => 
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.files?.some(f => f.name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      
      if (!matchesSearch && !hasMatchingFiles && !hasMatchingChildren) {
        return null;
      }
    }

    return (
      <div key={folder.id} className="select-none">
        <div
          className="flex items-center gap-2 py-2 px-3 hover:bg-gray-50 rounded-lg cursor-pointer group"
          style={{ paddingLeft: `${12 + depth * 20}px` }}
        >
          {/* Expand/Collapse */}
          <button 
            onClick={() => toggleFolder(folder.id)}
            className="p-0.5 hover:bg-gray-200 rounded"
          >
            {hasChildren ? (
              isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />
            ) : (
              <div className="w-4 h-4" />
            )}
          </button>

          {/* Folder Icon */}
          {isExpanded ? (
            <FolderOpen className="w-5 h-5" style={{ color: isPrivate ? '#6366f1' : enconOrange }} />
          ) : (
            <Folder className="w-5 h-5" style={{ color: isPrivate ? '#6366f1' : enconOrange }} />
          )}

          {/* Private indicator */}
          {isPrivate && <Lock className="w-3.5 h-3.5 text-indigo-500" />}

          {/* Folder Name */}
          <span className="flex-1 font-medium text-gray-700 truncate">{folder.name}</span>

          {/* Visibility Badge */}
          {getVisibilityBadge(folder.visibility)}

          {/* Owner */}
          {folder.owner_username && (
            <span className="text-xs text-gray-400">
              ({folder.owner_username})
            </span>
          )}

          {/* Settings Button */}
          {!isPrivate && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                openPermissionModal(folder, 'folder');
              }}
              className="p-1.5 hover:bg-gray-200 rounded opacity-0 group-hover:opacity-100 transition-opacity"
              title="Manage permissions"
            >
              <Settings className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>

        {/* Children & Files */}
        {isExpanded && (
          <div>
            {folder.children?.map(child => renderFolderTree(child, depth + 1))}
            {folder.files?.map(file => renderFileItem(file, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderFileItem = (file, depth = 0) => {
    // Apply search filter
    if (searchTerm && !file.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return null;
    }

    return (
      <div
        key={file.id}
        className="flex items-center gap-2 py-2 px-3 hover:bg-gray-50 rounded-lg group"
        style={{ paddingLeft: `${32 + depth * 20}px` }}
      >
        {/* File Icon */}
        {getFileIcon(file.file_type)}

        {/* File Name */}
        <span className="flex-1 text-sm text-gray-600 truncate">{file.name}</span>

        {/* Size */}
        <span className="text-xs text-gray-400">{formatFileSize(file.size)}</span>

        {/* Visibility Badge */}
        {getVisibilityBadge(file.visibility)}

        {/* Settings Button */}
        {!file.is_private && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              openPermissionModal(file, 'file');
            }}
            className="p-1.5 hover:bg-gray-200 rounded opacity-0 group-hover:opacity-100 transition-opacity"
            title="Manage permissions"
          >
            <Settings className="w-4 h-4 text-gray-500" />
          </button>
        )}
      </div>
    );
  };

  const folderTree = buildFolderTree();

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: enconOrange }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 rounded-lg bg-orange-100">
              <Folder className="w-6 h-6" style={{ color: enconOrange }} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{folders.length}</p>
              <p className="text-sm text-gray-500">Total Folders</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 rounded-lg bg-blue-100">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{files.length}</p>
              <p className="text-sm text-gray-500">Total Files</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 rounded-lg bg-indigo-100">
              <Lock className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{privateFolders.length}</p>
              <p className="text-sm text-gray-500">Private Folders</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 rounded-lg bg-green-100">
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{users.length}</p>
              <p className="text-sm text-gray-500">Total Users</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Private Folders Overview */}
      {privateFolders.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-600" />
              User Private Folders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {privateFolders.map(folder => (
                <div key={folder.id} className="flex items-center gap-3 p-3 bg-indigo-50 rounded-lg">
                  <Folder className="w-5 h-5 text-indigo-500" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-700 truncate">{folder.name}</p>
                    <p className="text-xs text-gray-500">{folder.file_count || 0} files</p>
                  </div>
                  <UserCheck className="w-4 h-4 text-indigo-400" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* File Explorer Tree */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">All Files & Folders</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search files and folders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="files-admin-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg divide-y max-h-[500px] overflow-y-auto">
            {folderTree.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Folder className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No files or folders found</p>
              </div>
            ) : (
              folderTree.map(folder => renderFolderTree(folder))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Permission Modal */}
      <Dialog open={permissionModalOpen} onOpenChange={setPermissionModalOpen}>
        <DialogContent className="bg-white max-w-md" style={{ borderRadius: '16px' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" style={{ color: enconOrange }} />
              Manage Permissions
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              {selectedItemType === 'folder' ? (
                <Folder className="w-5 h-5" style={{ color: enconOrange }} />
              ) : (
                getFileIcon(selectedItem?.file_type)
              )}
              <span className="font-medium truncate">{selectedItem?.name}</span>
            </div>

            <div className="space-y-2">
              <Label>Visibility</Label>
              <Select value={editVisibility} onValueChange={setEditVisibility}>
                <SelectTrigger data-testid="visibility-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="global">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-green-600" />
                      Global (Everyone)
                    </div>
                  </SelectItem>
                  <SelectItem value="restricted">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-yellow-600" />
                      Restricted (Selected Users)
                    </div>
                  </SelectItem>
                  <SelectItem value="hidden">
                    <div className="flex items-center gap-2">
                      <EyeOff className="w-4 h-4 text-red-600" />
                      Hidden (Super Admin Only)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {editVisibility === 'restricted' && (
              <div className="space-y-2">
                <Label>Allowed Users</Label>
                <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {users.map(user => (
                    <label 
                      key={user.id} 
                      className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={editAllowedUsers.includes(user.id) || editAllowedUsers.includes(user.username)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditAllowedUsers([...editAllowedUsers, user.id]);
                          } else {
                            setEditAllowedUsers(editAllowedUsers.filter(id => id !== user.id && id !== user.username));
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm">{user.full_name || user.username}</span>
                      <span className="text-xs text-gray-400">({user.email || user.username})</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPermissionModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={savePermissions} 
              style={{ backgroundColor: enconOrange }}
              className="text-white"
              data-testid="save-permissions-btn"
            >
              Save Permissions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FilesAdmin;
